'use client'
import { CircleHelp } from 'lucide-react'
import { Slider } from '@/components/ui/slider'
import formatNumber from '@/utils/format-number'
import { decimalUnits, formatUnits, planTapListings } from '@/utils/tap-listing-plan'

interface Props {
	busy?: boolean
	amount: string
	setAmount: (value: string) => void
	available: string
	decimals: number
	outputs: number
	setOutputs: (value: number) => void
	recommended: number
	plan?: ReturnType<typeof planTapListings>
	error: string
	usd: (sats: number) => string
}
export default function TapListingFields({
	busy,
	amount,
	setAmount,
	available,
	decimals,
	outputs,
	setOutputs,
	recommended,
	plan,
	error,
	usd
}: Props) {
	const availableUnits = decimalUnits(available, decimals)
	const units = decimalUnits(amount, decimals)
	// Radix uses numbers; keep the balance and conversion in exact token atoms.
	const sliderMax = Number(availableUnits > 1000n ? 1000n : availableUnits) || 1
	const sliderValue = availableUnits > 0n ? Number((units * BigInt(sliderMax)) / availableUnits) : 0
	const quantity = plan?.amounts[0]
	return (
		<fieldset disabled={busy} className="min-w-0">
			<div className="flex p-2 flex-col w-full gap-2">
				<p className="text-xs break-words">Number of tokens to split: {formatNumber(amount)}</p>
				<Slider
					thumbLabel="Number of tokens to split"
					value={[sliderValue]}
					max={sliderMax}
					min={0}
					step={1}
					disabled={busy || availableUnits === 0n}
					onValueChange={([value]) =>
						setAmount(formatUnits((availableUnits * BigInt(value)) / BigInt(sliderMax), decimals))
					}
					className="[&_[role=slider]]:size-3.5"
				/>
			</div>
			<div className="flex items-center gap-2 p-2">
				<div className="flex items-center gap-1 shrink-0">
					<label htmlFor="tap-outputs" className="text-xs whitespace-nowrap">
						Number of Outputs:
					</label>
					<span title="Choose how many lots to create. Smaller lots are suggested based on your token balance and floor price.">
						<CircleHelp className="size-3 text-muted-foreground" aria-hidden="true" />
					</span>
				</div>
				<div className="flex-1 min-w-0">
					<Slider
						thumbLabel="Output count"
						value={[outputs]}
						max={24}
						min={1}
						step={1}
						disabled={busy}
						onValueChange={([value]) => setOutputs(value)}
						className="[&_[role=slider]]:size-3.5"
					/>
				</div>
				<input
					id="tap-outputs"
					type="number"
					min={1}
					max={24}
					value={outputs}
					onChange={(e) => {
						const value = parseInt(e.target.value, 10)
						if (Number.isFinite(value)) setOutputs(Math.max(1, Math.min(24, value)))
					}}
					className="w-12 h-7 text-center text-xs font-medium text-foreground bg-background border border-border rounded focus:outline-none focus:border-border transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
				/>
			</div>
			<div className="mx-2 flex items-center justify-between gap-2 pb-2" aria-live="polite">
				<p className="text-xs font-semibold text-foreground">Quantity Per Lot</p>
				<p className="text-xs font-semibold text-muted-foreground text-right">
					{quantity ? `${formatNumber(quantity)} / ${usd(plan!.gross / outputs)}` : '—'}
				</p>
			</div>
			<p className="px-2 pb-2 text-xxs text-muted-foreground">
				Suggested: {recommended} {recommended === 1 ? 'output' : 'outputs'} based on floor price.
				Adjust to suit.
			</p>
			{error && (
				<p role="status" className="px-2 pb-2 text-xs text-amber-500">
					{error}
				</p>
			)}
		</fieldset>
	)
}
